import AuthModalButton from 'components/AuthModalButton/AuthModalButton';
import { useApi } from 'contexts/Api/Api';
import { useContent } from 'contexts/Content/Content';
import { getPathname, getUrl } from 'helpers';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Container,
    Content,
    Logo,
    LogoMobile,
    LogoPicture,
    LogoWrap,
    PortionMarking,
    Tagline,
    Text,
    Title,
    UserActionLink,
    UserActions,
    UserContainer,
    UserImage,
    UserName,
    UserPicture,
    UserProfile,
    UserProfileMenu,
    UserProfileMenuButton,
    UserProfileMenuLink,
    Wrap,
} from './Header.styled';

export type HeaderAuthState = 'landing_guest' | 'landing_member' | 'lesson_member';

export function getHeaderAuthState(path: string, authEnabled: boolean, authenticated: boolean): HeaderAuthState | null {
    if (!authEnabled) return null;
    if (path === '/') return authenticated ? 'landing_member' : 'landing_guest';
    // Anonymous visitors never reach a /lessons/* route (#171 gate) — they are
    // redirected to landing before this header can render for that path.
    if (path.startsWith('/lessons/')) return authenticated ? 'lesson_member' : null;
    return null;
}

export default function Header({ children, style, visible = true }: HeaderProps) {
    const [openProfileMenu, setOpenProfileMenu] = useState(false);
    const api = useApi();
    const content = useContent();
    const router = useRouter();
    const userContainerRef = useRef<HTMLDivElement>(null);

    const { background, backgroundMobile, foreground, logo, logoLink, logoMobile, tagline, textOffset, title } =
        content.header;
    const { portionMarking } = content.general;

    const headerAuthState = useMemo(() => {
        const path = getPathname(router.asPath);
        return getHeaderAuthState(path, api.authenticate, api.authenticated);
    }, [api.authenticate, api.authenticated, router.asPath]);

    const handleClick = useCallback(
        (e: MouseEvent) => {
            if (openProfileMenu && userContainerRef.current && !userContainerRef.current.contains(e.target as Node)) {
                setOpenProfileMenu(false);
            }
        },
        [openProfileMenu]
    );

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            setOpenProfileMenu(false);
        }
    }, []);

    useEffect(() => {
        window.addEventListener('click', handleClick);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('click', handleClick);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleClick, handleKeyDown]);

    function handleLogout() {
        void api.logout();
        setOpenProfileMenu(false);
    }

    return (
        <Wrap style={style}>
            {portionMarking && <PortionMarking>{portionMarking}</PortionMarking>}
            {visible && (
                <Container $background={background} $backgroundMobile={backgroundMobile || background}>
                    <LogoWrap>
                        <a aria-label="Home" data-testid="header-logo-link" href={logoLink || '/'}>
                            <LogoPicture>
                                <LogoMobile alt="" aria-hidden src={getUrl(logoMobile || logo)} />
                                <Logo alt="" aria-hidden src={getUrl(logo)} />
                            </LogoPicture>
                        </a>
                    </LogoWrap>
                    <Content $foreground={foreground}>
                        <Text
                            aria-label={title}
                            data-testid="header-title-link"
                            href={getUrl('/')}
                            $foreground={foreground}
                            $offset={textOffset}
                        >
                            <Title>{title}</Title>
                            {tagline && <Tagline>{tagline}</Tagline>}
                        </Text>
                        {children}
                        {headerAuthState !== null && (
                            <>
                                {headerAuthState === 'landing_guest' ? (
                                    <UserActions>
                                        <AuthModalButton
                                            aria-label="Open login modal"
                                            data-testid="header-login-button"
                                            variant="white"
                                            onClick={api.showAuthModal}
                                        >
                                            Log in
                                        </AuthModalButton>
                                        <AuthModalButton
                                            aria-label="Open registration modal"
                                            data-testid="header-join-button"
                                            onClick={() => api.showAuthModal('registration')}
                                        >
                                            Join Now
                                        </AuthModalButton>
                                    </UserActions>
                                ) : (
                                    <UserActions>
                                        {headerAuthState === 'landing_member' && (
                                            <UserActionLink
                                                aria-label="Go to My Kit"
                                                data-testid="header-my-kit-link"
                                                href={getUrl('/mykit')}
                                            >
                                                My Kit
                                            </UserActionLink>
                                        )}
                                        <UserContainer ref={userContainerRef}>
                                            <UserProfile
                                                aria-expanded={openProfileMenu}
                                                aria-haspopup="menu"
                                                aria-label="Account menu"
                                                data-testid="header-account-menu-button"
                                                onClick={() => setOpenProfileMenu(!openProfileMenu)}
                                                type="button"
                                            >
                                                <UserName>{api.userInfo?.first_name || 'Account'}</UserName>
                                                <UserPicture>
                                                    <UserImage
                                                        alt=""
                                                        aria-hidden
                                                        src={api.userInfo?.avatar || getUrl('/assets/user-icon.svg')}
                                                    />
                                                </UserPicture>
                                            </UserProfile>
                                            {openProfileMenu && (
                                                <UserProfileMenu
                                                    aria-label="Account options"
                                                    data-testid="header-account-menu"
                                                    role="menu"
                                                >
                                                    <UserProfileMenuLink
                                                        data-testid="header-my-profile-link"
                                                        href={getUrl('/user')}
                                                        role="menuitem"
                                                    >
                                                        My Profile
                                                    </UserProfileMenuLink>
                                                    <UserProfileMenuButton
                                                        data-testid="header-logout-button"
                                                        onClick={handleLogout}
                                                        role="menuitem"
                                                        type="button"
                                                    >
                                                        Log Out
                                                    </UserProfileMenuButton>
                                                </UserProfileMenu>
                                            )}
                                        </UserContainer>
                                    </UserActions>
                                )}
                            </>
                        )}
                    </Content>
                </Container>
            )}
        </Wrap>
    );
}
